import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { publicAccountsService } from "../services/publicAccounts.service";
import {
  PUBLIC_ACCOUNTS_PAGE_SIZE,
  SEARCH_OVERLAY_DURATION_MS,
} from "../constants/public-accounts.constants";
import { groupPublicAccountsIntoSections } from "../utils/mapPublicAccountToDisplay";
import type { PublicAccountItem } from "../types/public-accounts-api.types";
import type { AccountSectionDisplay } from "../types/accounts-wallets.types";

export function useAccountsWalletsPage(searchParam: string) {
  const [items, setItems] = useState<PublicAccountItem[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [searchOverlayKeep, setSearchOverlayKeep] = useState(false);
  const lastSearchParamRef = useRef<string>("");
  const overlayKeepRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sections = useMemo<AccountSectionDisplay[]>(
    () => groupPublicAccountsIntoSections(items),
    [items]
  );

  const fetchAccounts = useCallback(async (searchTerm: string) => {
    setIsLoading(true);
    setError(null);
    setHasNoAccess(false);
    try {
      const term = searchTerm.trim() || undefined;
      const response = await publicAccountsService.fetchList({
        skip: 0,
        limit: PUBLIC_ACCOUNTS_PAGE_SIZE,
        search: term,
      });
      lastSearchParamRef.current = searchTerm;
      setItems(response.data);
      setTotal(response.total);
      setSkip(response.data.length);
      setHasMore(response.hasMore ?? false);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setHasNoAccess(true);
        setItems([]);
        setTotal(0);
        setHasMore(false);
        return;
      }
      const message = HttpApiService.getErrorMessage(err);
      setError(message ?? "Error al cargar las cuentas");
      setItems([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      if (searchTerm.trim() !== "") {
        if (overlayKeepRef.current) clearTimeout(overlayKeepRef.current);
        setSearchOverlayKeep(true);
        overlayKeepRef.current = setTimeout(() => {
          setSearchOverlayKeep(false);
          overlayKeepRef.current = null;
        }, SEARCH_OVERLAY_DURATION_MS);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (overlayKeepRef.current) clearTimeout(overlayKeepRef.current);
    };
  }, []);

  useEffect(() => {
    fetchAccounts(searchParam);
  }, [searchParam, fetchAccounts]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    setError(null);
    try {
      const term = lastSearchParamRef.current.trim() || undefined;
      const response = await publicAccountsService.fetchList({
        skip,
        limit: PUBLIC_ACCOUNTS_PAGE_SIZE,
        search: term,
      });
      setItems((prev) => [...prev, ...response.data]);
      setTotal(response.total);
      setSkip((s) => s + response.data.length);
      setHasMore(response.hasMore ?? false);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setHasNoAccess(true);
        return;
      }
      const message = HttpApiService.getErrorMessage(err);
      setError(message ?? "Error al cargar más cuentas");
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, skip]);

  return {
    sections,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    hasNoAccess,
    searchOverlayKeep,
    fetchAccounts,
    loadMore,
  };
}
